import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { parseJsonObject } from "@/types/domain";
import type { Json } from "@/integrations/supabase/types";

/**
 * Read a global setting from the `app_config` table, parsed into `T`.
 *
 * Shared by every consumer of a global flag (emergency-mode banner, Spotify
 * artist-images gate, admin toggles) so they hit one cached query per key
 * instead of each re-implementing the same read.
 */
export function useAppConfig<T extends object>(
  key: string,
  fallback: T,
  options?: { refetchInterval?: number },
) {
  return useQuery<T>({
    queryKey: ["app-config", key],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("app_config")
        .select("value")
        .eq("key", key)
        .maybeSingle();

      // Throw on a real error so React Query keeps the last good (cached) value
      // and retries, instead of overwriting it with the fallback — a fetch that
      // fails on flaky connectivity must not flip a live flag off. A genuinely
      // missing row (data null, no error) falls back.
      if (error) throw error;
      return parseJsonObject<T>(data?.value ?? null, fallback);
    },
    staleTime: 30_000,
    refetchInterval: options?.refetchInterval,
  });
}

/**
 * Update a global setting in `app_config` (admin-only via RLS). Invalidates the
 * matching `useAppConfig(key)` cache on success so every reader refreshes.
 */
export function useUpdateAppConfig<T extends object>(key: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (value: T) => {
      const { error } = await supabase
        .from("app_config")
        .update({ value: value as Json, updated_at: new Date().toISOString() })
        .eq("key", key);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["app-config", key] });
    },
  });
}
